class HeaderManager {
  constructor() {
    this.header = document.querySelector('.site-header');
    this.stickyHeader = document.querySelector('.header-sticky');
    this.menuTrigger = this.header?.dataset.menuTrigger || 'hover';
    this.isVoiceSearchSupported = 'webkitSpeechRecognition' in window || 'SpeechRecognition' in window;
    this.isPointerDown = false;
    this._boundMouseUp = () => { this.isPointerDown = false; };
    this._boundDocumentClick = (e) => {
      if (!e.target.closest('.menu-item') && !e.target.closest('.submenu-item-link-list')) {
        this.closeAllMenuItems();
      }
    };
    this._boundDocumentKeydown = (e) => {
      if (e.key === 'Escape') {
        this.closeAllMenuItems();
      }
    };

    this.init();
    this.updateHeaderHeight();
    window.addEventListener('resize', () => this.updateHeaderHeight());
  }

  updateHeaderHeight() {
    if (!this.header) return;
    const height = this.header.offsetHeight;
    document.documentElement.style.setProperty('--header-height', `${height}px`);

    const announcement = document.querySelector(".announcement-bar-section");
    const announcementHeight = announcement ? announcement.offsetHeight : 0;
    document.documentElement.style.setProperty('--announcement-height', `${announcementHeight}px`);
  }

  init() {
    this._megaMenuHoverTimeout = null;
    this.setupMenuInteractions();
    this.setupSearchFunctionality();
    this.setupDrawerMenu();
    this.setupVoiceSearch();
    this.setupCartUpdates();
    this.setupStickyHeader();
  }

  destroy() {
    document.removeEventListener('mouseup', this._boundMouseUp);
    document.removeEventListener('click', this._boundDocumentClick);
    document.removeEventListener('keydown', this._boundDocumentKeydown);

    if (this._boundEscapeSearch) {
      document.removeEventListener('keydown', this._boundEscapeSearch);
    }

    if (this.stickyHeaderObserver) {
      this.stickyHeaderObserver.disconnect();
    }
  }

  setupMenuInteractions() {
    if (!this.header) return;

    this.header.addEventListener('click', (e) => {
      const isMobile = window.innerWidth < 1025;
      const menuItem = e.target.closest('.menu-item, .submenu-item-link-list');
      const link = e.target.closest('.menu-link, .dropdown-link');

      if (!menuItem || !link) return;

      const hasSubmenu = menuItem.classList.contains('has-mega-menu') ||
        menuItem.classList.contains('has-dropdown') ||
        menuItem.classList.contains('submenu-item-link-list');

      if (!hasSubmenu || !link.hasAttribute('aria-haspopup')) return;

      const isExpanded = link.getAttribute('aria-expanded') === 'true' || menuItem.classList.contains('menu-active');

      if (this.menuTrigger === 'click' || isMobile) {
        e.preventDefault();
        e.stopPropagation();
        this.toggleMenuItem(menuItem);
      }
    });

    const menuItems = this.header.querySelectorAll('.menu-item, .submenu-item-link-list');
    menuItems.forEach(item => {
      item.addEventListener('mouseenter', () => {
        if (this.menuTrigger === 'hover' && window.innerWidth >= 1025) {
          clearTimeout(this._megaMenuHoverTimeout);

          const parent = item.parentElement;
          if (parent) {
            Array.from(parent.children).forEach(sibling => {
              if (sibling !== item && sibling.classList.contains('menu-active')) {
                this.closeMenuItem(sibling);
              }
            });
          }

          this.openMenuItem(item);
        }
      });

      item.addEventListener('mouseleave', () => {
        if (this.menuTrigger === 'hover' && window.innerWidth >= 1025) {
          if (item.classList.contains('has-mega-menu')) {
            this._megaMenuHoverTimeout = setTimeout(() => {
              this.closeMenuItem(item);
            }, 500);
          } else {
            this.closeMenuItem(item);
          }
        }
      });

      item.addEventListener('focusin', () => {
        if (window.innerWidth >= 1025 && this.menuTrigger === 'hover' && !this.isPointerDown) {
          this.openMenuItem(item);
        }
      });

      item.addEventListener('focusout', (e) => {
        if (window.innerWidth >= 1025 && this.menuTrigger === 'hover' && !item.contains(e.relatedTarget)) {
          this.closeMenuItem(item);
        }
      });
    });

    document.addEventListener('mouseup', this._boundMouseUp);
    document.addEventListener('click', this._boundDocumentClick);
    document.addEventListener('keydown', this._boundDocumentKeydown);
  }

  toggleMenuItem(item) {
    const link = item.querySelector('.menu-link, .dropdown-link');
    const isExpanded = link?.getAttribute('aria-expanded') === 'true';

    if (isExpanded) {
      this.closeMenuItem(item);
    } else {
      const parent = item.parentElement;
      const siblings = parent.children;
      Array.from(siblings).forEach(sibling => {
        if (sibling !== item && (sibling.classList.contains('menu-item') || sibling.classList.contains('submenu-item-link-list'))) {
          this.closeMenuItem(sibling);
        }
      });
      this.openMenuItem(item);
    }
  }

  openMenuItem(item) {
    const link = item.querySelector('.menu-link, .dropdown-link');
    if (!link) return;

    item.classList.add('menu-active');
    link.setAttribute('aria-expanded', 'true');

    let parent = item.parentElement.closest('.menu-item, .submenu-item-link-list');
    if (parent && !parent.classList.contains('menu-active')) {
      this.openMenuItem(parent);
    }

    if (item.classList.contains('menu-item')) {
      if (item.classList.contains('has-mega-menu')) {
        document.body.classList.add('menu-open');
        document.body.classList.remove('has-dropdown-open');
        this.header?.classList.add('menu-is-open');
      } else if (item.classList.contains('has-dropdown')) {
        document.body.classList.add('has-dropdown-open');
        const rect = item.getBoundingClientRect();
        item.style.setProperty('--dropdown-left', `${rect.left}px`);
      }
    }
  }

  closeMenuItem(item) {
    const link = item.querySelector('.menu-link, .dropdown-link');
    if (!link) return;

    item.classList.remove('menu-active');
    link.setAttribute('aria-expanded', 'false');

    const activeChildren = item.querySelectorAll('.menu-active');
    activeChildren.forEach(child => this.closeMenuItem(child));

    if (!document.querySelector('.menu-item.menu-active')) {
      document.body.classList.remove('menu-open');
      document.body.classList.remove('has-dropdown-open');
    }

    if (!document.querySelector('.menu-item.has-mega-menu.menu-active')) {
      this.header?.classList.remove('menu-is-open');
    }
  }

  closeAllMenuItems() {
    const mainMenuItems = document.querySelectorAll('.menu-item.menu-active');
    mainMenuItems.forEach(item => this.closeMenuItem(item));
  }

  setupSearchFunctionality() {
    const searchToggle = document.querySelector('[data-search-toggle]');
    const searchOverlay = document.getElementById('search-overlay');
    const searchClose = document.querySelector('.search-close');
    const searchInputs = document.querySelectorAll('.search-input');
    const searchForm = document.querySelector('.search-form');

    if (!searchToggle || !searchOverlay) return;

    searchToggle.addEventListener('click', () => {
      this.openSearch();
    });

    searchClose?.addEventListener('click', () => {
      this.closeSearch();
    });

    searchOverlay.addEventListener('click', (e) => {
      if (e.target === searchOverlay) {
        this.closeSearch();
      }
    });

    this._boundEscapeSearch = (e) => {
      if (e.key === 'Escape') {
        this.closeSearch();
      }
    };
    document.addEventListener('keydown', this._boundEscapeSearch);

    searchForm?.addEventListener('submit', (e) => {
      const activeInput = document.querySelector('.search-input:focus') || searchInputs[0];
      const query = activeInput?.value.trim();
      if (!query) {
        e.preventDefault();
        return;
      }
    });

    searchInputs.forEach(searchInput => {
      let searchTimeout;
      searchInput.addEventListener('input', (e) => {
        clearTimeout(searchTimeout);
        const query = e.target.value.trim();
        searchInputs.forEach(input => {
          if (input !== searchInput) input.value = query;
        });

        if (query.length > 0) {
          this.hideDefaultSuggestions();
          searchTimeout = setTimeout(() => {
            this.performLiveSearch(query);
          }, 300);
        } else {
          this.showDefaultSuggestions();
          this.clearLiveSearchResults();
        }
      });

      searchInput.addEventListener('focus', (e) => {
        const query = e.target.value.trim();
        if (!query) this.showDefaultSuggestions();
      });
    });
  }

  openSearch() {
    const searchOverlay = document.getElementById('search-overlay');
    const searchInputs = document.querySelectorAll('.search-input');
    this.lastFocusedElement = document.activeElement;
    searchOverlay.classList.add('active');
    document.body.style.overflow = 'hidden';
    this.showDefaultSuggestions();
    setTimeout(() => {
      searchInputs[0]?.focus();
      this.trapFocus(searchOverlay);
    }, 300);
  }

  closeSearch() {
    const searchOverlay = document.getElementById('search-overlay');
    const searchInputs = document.querySelectorAll('.search-input');
    if (!searchOverlay) return;
    searchOverlay.classList.remove('active');
    document.body.style.overflow = '';
    searchInputs.forEach(input => { input.value = ''; });
    this.showDefaultSuggestions();
    this.clearLiveSearchResults();
    if (this.lastFocusedElement) this.lastFocusedElement.focus();
  }

  trapFocus(container) {
    const focusableElements = Array.from(container.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'))
      .filter(el => {
        const style = window.getComputedStyle(el);
        return style.display !== 'none' && style.visibility !== 'hidden' && el.offsetParent !== null;
      });
    if (focusableElements.length === 0) return;
    const firstFocusableElement = focusableElements[0];
    const lastFocusableElement = focusableElements[focusableElements.length - 1];
    const handleKeyDown = (e) => {
      if (e.key !== 'Tab') return;
      if (e.shiftKey) {
        if (document.activeElement === firstFocusableElement) {
          lastFocusableElement.focus();
          e.preventDefault();
        }
      } else {
        if (document.activeElement === lastFocusableElement) {
          firstFocusableElement.focus();
          e.preventDefault();
        }
      }
    };
    container.removeEventListener('keydown', container._focusTrapListener);
    container._focusTrapListener = handleKeyDown;
    container.addEventListener('keydown', handleKeyDown);
  }

  showDefaultSuggestions() {
    const defaultSuggestions = document.getElementById('default-suggestions');
    const resultsContainer = document.getElementById('search-results');
    if (defaultSuggestions) defaultSuggestions.style.display = 'block';
    if (resultsContainer) resultsContainer.classList.add('has-results');
  }

  hideDefaultSuggestions() {
    const defaultSuggestions = document.getElementById('default-suggestions');
    if (defaultSuggestions) defaultSuggestions.style.display = 'none';
  }

  async performLiveSearch(query) {
    try {
      const response = await fetch(`/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product,article,page,collection&resources[limit]=10&resources[options][fields]=title,body,product_type,variants.title`);
      const data = await response.json();
      this.displaySearchResults(data);
    } catch (error) { }
  }

  displaySearchResults(data) {
    const liveResultsContainer = document.getElementById('live-search-results');
    const noResultsElement = document.getElementById('predictive-no-results');
    const noResultsMessage = document.getElementById('predictive-no-results-message');
    if (!liveResultsContainer) return;
    let html = '';
    let hasResults = false;
    if (data.resources && data.resources.results) {
      const { products = [], collections = [], articles = [], pages = [] } = data.resources.results;
      const placeholderImg = `<div class="predictive-search-placeholder"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clip-rule="evenodd" /></svg></div>`;

      const getImgSrc = (item) => {
        if (!item) return null;
        let img = item.featured_image || item.image || item.featured_media?.preview_image || item.featured_media;
        if (typeof img === 'string') return img;
        if (img && typeof img === 'object') {
          return img.url || img.src || img.preview_image?.url;
        }
        return null;
      };

      if (products.length > 0) {
        hasResults = true;
        html += `<div class="search-section"><h3>${window.themeVariables?.strings?.searchProducts || 'Products'}</h3><ul class="search-results-items">`;
        products.forEach(product => {
          const imgSrc = getImgSrc(product);
          const imageTag = imgSrc ? `<img src="${imgSrc}" alt="${product.title}" loading="lazy" width="60" height="60">` : placeholderImg;
          const formattedPrice = (typeof window.formatMoney === 'function') ? window.formatMoney(product.price) : `${(product.price / 100).toFixed(2)}`;
          html += `<li class="search-result-item"><a href="${product.url}">${imageTag}<div class="search-item-info"><span class="search-item-title">${product.title}</span><div class="search-result-item-meta"><span>${formattedPrice}</span></div></div></a></li>`;
        });
        html += '</ul></div>';
      }

      if (collections.length > 0) {
        hasResults = true;
        html += `<div class="search-section"><h3>${window.themeVariables?.strings?.searchCollections || 'Collections'}</h3><ul class="search-results-items">`;
        collections.forEach(collection => {
          const imgSrc = getImgSrc(collection);
          const imageTag = imgSrc ? `<img src="${imgSrc}" alt="${collection.title}" loading="lazy" width="60" height="60">` : placeholderImg;
          html += `<li class="search-result-item"><a href="${collection.url}">${imageTag}<div class="search-item-info"><span class="search-item-title">${collection.title}</span></div></a></li>`;
        });
        html += '</ul></div>';
      }

      if (articles.length > 0) {
        hasResults = true;
        html += `<div class="search-section"><h3>${window.themeVariables?.strings?.searchArticles || 'Articles'}</h3><ul class="search-results-items">`;
        articles.forEach(article => {
          const imgSrc = getImgSrc(article);
          const imageTag = imgSrc ? `<img src="${imgSrc}" alt="${article.title}" loading="lazy" width="60" height="60">` : placeholderImg;
          html += `<li class="search-result-item"><a href="${article.url}">${imageTag}<div class="search-item-info"><span class="search-item-title">${article.title}</span></div></a></li>`;
        });
        html += '</ul></div>';
      }

      if (pages.length > 0) {
        hasResults = true;
        html += `<div class="search-section"><h3>${window.themeVariables?.strings?.searchPages || 'Pages'}</h3><ul class="search-results-items">`;
        pages.forEach(page => {
          html += `<li class="search-result-item"><a href="${page.url}">${placeholderImg}<div class="search-item-info"><span class="search-item-title">${page.title}</span></div></a></li>`;
        });
        html += '</ul></div>';
      }

      if (hasResults) {
        const searchInput = document.querySelector('.search-input');
        const query = searchInput ? searchInput.value.trim() : '';
        html += `<div class="search-view-all"><a href="/search?q=${encodeURIComponent(query)}">${window.themeVariables?.strings?.searchViewAll || 'View all results for'} "${query}"</a></div>`;
        if (noResultsElement) noResultsElement.style.display = 'none';
      } else {
        if (noResultsElement) {
          const searchInput = document.querySelector('.search-input');
          const query = searchInput ? searchInput.value.trim() : '';
          if (noResultsMessage) noResultsMessage.textContent = `No results found for "${query}"`;
          noResultsElement.style.display = 'block';
        }
      }
    }
    liveResultsContainer.innerHTML = html;
  }

  clearLiveSearchResults() {
    const liveResultsContainer = document.getElementById('live-search-results');
    if (liveResultsContainer) liveResultsContainer.innerHTML = '';
  }

  formatMoney(cents) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    const amount = (cents / 100).toFixed(2);
    const format = window.themeVariables?.settings?.moneyFormat || '${{amount}}';
    return format.replace(/\{\{\s*amount\s*\}\}/, amount);
  }

  setupVoiceSearch() {
    if (!this.isVoiceSearchSupported) return;
    const voiceBtns = document.querySelectorAll('.voice-search-btn');
    voiceBtns.forEach(voiceBtn => {
      voiceBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.startVoiceSearch();
      });
    });
  }

  startVoiceSearch() {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) return;
    const recognition = new SpeechRecognition();
    const voiceBtns = document.querySelectorAll('.voice-search-btn');
    const searchInputs = document.querySelectorAll('.search-input');
    recognition.lang = 'en-US';
    voiceBtns.forEach(btn => btn.classList.add('listening'));
    recognition.onresult = (event) => {
      const transcript = event.results[0][0].transcript;
      searchInputs.forEach(input => {
        input.value = transcript;
        input.dispatchEvent(new Event('input', { bubbles: true }));
      });
    };
    recognition.onend = () => voiceBtns.forEach(btn => btn.classList.remove('listening'));
    recognition.start();
  }

  setupDrawerMenu() {
    const drawerToggles = document.querySelectorAll('.drawer-toggle, .mobile-drawer-toggle');
    const drawerMenu = document.getElementById('drawer-menu');
    const drawerClose = document.querySelector('.drawer-close');
    const drawerOverlay = document.querySelector('.drawer-overlay');

    if (!drawerMenu) return;

    drawerToggles.forEach(toggle => {
      toggle.addEventListener('click', (e) => {
        e.preventDefault();
        this.openDrawer();
      });
    });

    drawerClose?.addEventListener('click', (e) => {
      e.preventDefault();
      this.closeDrawer();
    });

    drawerOverlay?.addEventListener('click', () => this.closeDrawer());
    this.setupDrawerMenuItems();
  }

  setupDrawerMenuItems() {
    const drawerMenu = document.getElementById('drawer-menu');
    if (!drawerMenu) return;

    drawerMenu.addEventListener('click', (e) => {
      const link = e.target.closest('[data-drawer-menu-toggle], [data-drawer-submenu-toggle]');
      const item = link?.closest('.drawer-menu-item.has-children, .drawer-submenu-item.has-children');

      if (item && link) {
        e.preventDefault();
        e.stopPropagation();
        this.toggleDrawerMenuItem(item);
      }
    });
  }

  toggleDrawerMenuItem(item) {
    const link = item.querySelector('.drawer-menu-link, .drawer-submenu-link');
    const isExpanded = item.classList.contains('expanded');

    if (isExpanded) {
      item.classList.remove('expanded');
      link?.setAttribute('aria-expanded', 'false');

      const activeChildren = item.querySelectorAll('.expanded');
      activeChildren.forEach(child => {
        child.classList.remove('expanded');
        child.querySelector('.drawer-menu-link, .drawer-submenu-link')?.setAttribute('aria-expanded', 'false');
      });
    } else {
      const parent = item.parentElement;
      const siblings = parent.children;
      Array.from(siblings).forEach(sibling => {
        if (sibling !== item) {
          sibling.classList.remove('expanded');
          sibling.querySelector('.drawer-menu-link, .drawer-submenu-link')?.setAttribute('aria-expanded', 'false');
        }
      });

      item.classList.add('expanded');
      link?.setAttribute('aria-expanded', 'true');
    }
  }

  openDrawer() {
    const drawerMenu = document.getElementById('drawer-menu');
    const drawerOverlay = document.querySelector('.drawer-overlay');
    document.body.style.overflow = 'hidden';
    drawerMenu?.classList.add('active');
    drawerOverlay?.classList.add('active');
  }

  closeDrawer() {
    const drawerMenu = document.getElementById('drawer-menu');
    const drawerOverlay = document.querySelector('.drawer-overlay');
    document.body.style.overflow = '';
    drawerMenu?.classList.remove('active');
    drawerOverlay?.classList.remove('active');
  }

  setupCartUpdates() {
    document.addEventListener('cart:updated', (e) => this.updateCartCount(e.detail.item_count));
    this.fetchCartCount();
  }

  async fetchCartCount() {
    try {
      const res = await fetch('/cart.js');
      const cart = await res.json();
      this.updateCartCount(cart.item_count);
    } catch (e) { }
  }

  updateCartCount(count) {
    const counts = document.querySelectorAll('[data-cart-count]');
    counts.forEach(el => {
      el.textContent = count;
      el.style.display = count > 0 ? 'flex' : 'none';
    });
  }

  setupStickyHeader() {
    if (!this.stickyHeader || this.stickyHeader.dataset.sticky !== 'true') return;
    let lastScroll = window.scrollY;
    window.addEventListener('scroll', () => {
      const currentScroll = window.scrollY;
      if (currentScroll > 100) {
        if (!this.stickyHeader.classList.contains('scrolled')) {
          this.stickyHeader.classList.add('scrolled');
          this.updateHeaderHeight();
        }
        this.stickyHeader.classList.toggle('hide', currentScroll > lastScroll && !document.body.classList.contains('menu-open'));
      } else {
        if (this.stickyHeader.classList.contains('scrolled')) {
          this.stickyHeader.classList.remove('scrolled', 'hide');
          this.updateHeaderHeight();
        }
      }
      lastScroll = currentScroll;
    }, { passive: true });
  }
}

let headerManagerInstance = null;
function initHeader() {
  if (headerManagerInstance) headerManagerInstance.destroy();
  headerManagerInstance = new HeaderManager();
}

document.addEventListener('DOMContentLoaded', initHeader);
document.addEventListener('shopify:section:load', (e) => {
  if (e.target.classList.contains('header-section') || e.target.querySelector('.site-header')) initHeader();
});

window.ThemeHeader = {
  openSearch: () => headerManagerInstance?.openSearch(),
  closeSearch: () => headerManagerInstance?.closeSearch(),
  updateCartCount: (count) => headerManagerInstance?.updateCartCount(count)
};

document.addEventListener("DOMContentLoaded", function () {
  const header = document.querySelector(".header-sticky");
  const announcement = document.querySelector(".announcement-bar-section");
  if (header && announcement && header.classList.contains("header-transparent")) {
    const setOffset = () => {
      if (!header.classList.contains("scrolled")) header.style.top = `${announcement.offsetHeight}px`;
    };
    setOffset();
    window.addEventListener("resize", setOffset, { passive: true });
    window.addEventListener("scroll", () => {
      if (window.scrollY > 50) {
        header.classList.add("scrolled");
        header.style.top = "0px";
      } else {
        header.classList.remove("scrolled");
        setOffset();
      }
    }, { passive: true });
  }
});